import React, { useEffect, useRef, useState } from "react";
import { ChevronRight, MenuSharp, OpenInNew } from "@mui/icons-material";
import { AmendmentIcon } from "@logora/parliament.icons.regular_icons";
import { ButtonBase } from "@mui/material";
import { capitalizeFirstLetter } from "../formatters";
import { Alinea } from "./Alinea";
import { AlertButton } from "../alerts";
import cx from "classnames";
import styles from "./TexteBox.module.scss";

const AmendementButton = ({ subdivision, onClick }) => {
	const [hovered, setHovered] = useState(false);
	const isActive = subdivision.nombreAmendements > 0;

	return (
		<button
			type="button"
			onClick={() => onClick(subdivision)}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			className={`${styles.amendmentButton} ${
				subdivision.nombreAmendements > 0 ? "active" : ""
			}`}
			disabled={subdivision.nombreAmendements === 0}
		>
			{subdivision.nombreAmendements}{" "}
			{isActive && hovered ? (
				<ChevronRight
					width={16}
					height={16}
					className={`${styles.chevronBtn}`}
				/>
			) : (
				<AmendmentIcon width={16} height={16} />
			)}
		</button>
	);
}

export const TexteBox = ({
	document,
	className,
	showAmendements = false,
	onAmendementButtonClick = () => {},
}) => {
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	let { alineas = [], subdivisions = [] } = document || {};
	const containerRef = useRef(null);
	const menuRef = useRef(null);

	subdivisions = subdivisions
		.filter((s) => s.type !== "ExposeMotifs")
		.sort((a, b) => a.sequence - b.sequence);

	const scrollToSubdivision = (hash) => {
		const element = containerRef.current.querySelector(`[data-hash="${hash}"]`);
		if (!element) return;
		element.scrollIntoView({ behavior: "smooth", block: "start" });
		setIsMenuOpen(false);
	};

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (menuRef.current && !menuRef.current.contains(event.target)) {
				setIsMenuOpen(false);
			}
		};

		window.addEventListener("mousedown", handleClickOutside);
		return () => {
			window.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	return (
		<div className={cx(styles.container, className)} ref={containerRef}>
			{subdivisions.length > 0 ? (
				<>
					<div className={styles.header}>
						<span className={styles.topTexte}>Texte</span>
						<div className={styles.headerRight}>
							{document?.pdfUrl && (
								<ButtonBase
									href={document?.pdfUrl}
									className={styles.originalTextButton}
									target="_blank"
								>
									<span>Voir l'original</span>
									<OpenInNew fontSize="small" />
								</ButtonBase>
							)}
							<button
								className={styles.summaryButton}
								onClick={() => setIsMenuOpen((isMenuOpen) => !isMenuOpen)}
								type="button"
							>
								<span>Sommaire</span>
								<MenuSharp fontSize="small" />
							</button>
							{isMenuOpen && (
								<div ref={menuRef} className={styles.menu}>
									{subdivisions.map((subdivision) => (
										<button
											key={subdivision.hash}
											onClick={() => scrollToSubdivision(subdivision.hash)}
											className={`${styles.menuItem} ${
												styles[subdivision.type?.toLowerCase()]
											}`}
											type="button"
										>
											{subdivision.titre}
										</button>
									))}
								</div>
							)}
						</div>
					</div>
					<div className={styles.content}>
						<h3 className={styles.formule}>
							{capitalizeFirstLetter(document?.titrePrincipal)}
						</h3>
						<div className={styles.subdivisions}>
							{subdivisions.map((subdivision, index) => (
								<div
									key={subdivision.hash}
									className={styles.subdivision}
									data-hash={subdivision.hash}
								>
									{showAmendements && (
										<div className={styles.actionButtons}>
											<AlertButton
												targetClass={"Subdivision"}
												targetId={subdivision.hash}
												size="small"
											/>
											<h5 className={styles.articleTitle}>
												{subdivision.titre}
											</h5>
											<AmendementButton
												subdivision={subdivision}
												onClick={onAmendementButtonClick}
											/>
										</div>
									)}
									<h5 className={styles.articleTitle}>
										{subdivision.sousTitre}
									</h5>

									{alineas.filter((a) => a.articleRefHash === subdivision.hash)
										.length > 0 && (
										<div className={styles.alineas}>
											{alineas
												.filter((a) => a.articleRefHash === subdivision.hash)
												.sort((a, b) => a.numero - b.numero)
												.map((alinea) => (
													<Alinea
														key={alinea.numero + 1}
														number={alinea.numero + 1}
														text={alinea.texte}
													/>
												))}
										</div>
									)}
								</div>
							))}
						</div>
					</div>
				</>
			) : document.pdfUrl ? (
				<>
					<object
						type="application/pdf"
						data={`${document.pdfUrl}#zoom=100`}
						height="100%"
						className={styles.pdf}
					>
						<div className={styles.empty}>Le document n'est pas disponible</div>
					</object>
				</>
			) : (
				<i className={styles.empty}>Le document n'est pas disponible</i>
			)}
		</div>
	);
};
